import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { AlertTriangle, Loader2, XCircle } from "lucide-react";

export const isOrderPaid = (o) => ["bezahlt", "abgeschlossen"].includes(o.status) || !!o.bezahlt_am;
const paidViaStripe = (o) => !!o.stripe_payment_intent_id && !o.bezahlt_manuell_von;

const fmtEur = (v) => Number(v || 0).toLocaleString("de-DE", { style: "currency", currency: "EUR" });

function RefundChoice({ order, value, onChange }) {
  const stripeOk = paidViaStripe(order);
  const opt = (key, label, hint, disabled) => (
    <label className={`flex gap-3 p-3 rounded-md border cursor-pointer transition-colors ${value === key ? "border-slate-900 bg-slate-50" : "border-slate-200 hover:bg-slate-50"} ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}>
      <input type="radio" name="erstattung" className="mt-1 accent-slate-900" checked={value === key} disabled={disabled} onChange={() => onChange(key)} data-testid={`storno-erstattung-${key}`} />
      <span><span className="text-sm font-medium text-slate-900">{label}</span><span className="block text-xs text-slate-500 mt-0.5">{hint}</span></span>
    </label>
  );
  return (
    <fieldset className="space-y-2" data-testid="storno-erstattung">
      <legend className="text-sm font-semibold text-slate-900 mb-1">Erstattung (Pflichtauswahl)</legend>
      {opt("stripe", "Zahlung über Stripe vollständig erstatten", stripeOk ? `${fmtEur(order.summe_brutto)} werden über Stripe an den Kunden zurückgezahlt.` : "Nicht verfügbar – der Auftrag wurde nicht über Stripe bezahlt.", !stripeOk)}
      {opt("manuell", "Keine automatische Erstattung (z. B. Überweisung – Erstattung manuell / bereits erstattet / Gutschrift)", "Es wird eine Aufgabe „Erstattung veranlassen“ für Sie angelegt.", false)}
    </fieldset>
  );
}

// Storno-Dialog: unbezahlt = einfache Bestätigung; bezahlt = Warnung + Pflichtauswahl Erstattung + Stornorechnung + Kundenmail.
export function CancelOrderDialog({ order, open, onOpenChange, onDone }) {
  const paid = isOrderPaid(order);
  const [erstattung, setErstattung] = useState(null);
  const [stornorechnung, setStornorechnung] = useState(true);
  const [mail, setMail] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/auftraege/${order.id}/stornieren`, paid ? { erstattung, stornorechnung: !!order.sevdesk_rechnung_id && stornorechnung, kunde_informieren: mail } : undefined);
      toast.success("Auftrag storniert.");
      if (r.data.sevdesk_warning && paid) toast.warning(r.data.sevdesk_warning);
      onDone(r.data);
      onOpenChange(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Stornieren fehlgeschlagen."); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { setErstattung(null); setMail(false); setStornorechnung(true); } onOpenChange(o); }}>
      <DialogContent className="max-w-xl" data-testid="storno-dialog">
        <DialogHeader>
          <DialogTitle>Auftrag {order.auftragsnummer} stornieren?</DialogTitle>
          <DialogDescription>{paid ? "Dieser Auftrag ist bereits bezahlt." : "Der Auftrag wird als storniert markiert."}</DialogDescription>
        </DialogHeader>
        {paid && (
          <div className="space-y-4">
            <div className="flex gap-2 p-3 rounded-md border-2 border-amber-300 bg-amber-50 text-amber-900 text-sm" data-testid="storno-paid-warning">
              <AlertTriangle className="w-5 h-5 shrink-0" />
              <div>
                <b>Achtung: bezahlter Auftrag ({fmtEur(order.summe_brutto)}).</b>
                {order.rechnungsnummer && <> Rechnung <span className="font-mono">{order.rechnungsnummer}</span> wurde bereits erstellt.</>}
                {order.status === "abgeschlossen" && <> Der Auftrag ist bereits abgeschlossen (versendet).</>}
                {" "}Die Stornierung lässt sich nur durch Reaktivieren rückgängig machen.
              </div>
            </div>
            {order.kostenlos
              ? <p className="text-sm text-slate-600" data-testid="storno-kostenlos-hint">Kostenlose Lieferung (0,00 €) – es ist keine Erstattung nötig.</p>
              : <RefundChoice order={order} value={erstattung} onChange={setErstattung} />}
            {order.sevdesk_rechnung_id && (
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" className="accent-slate-900 w-4 h-4" checked={stornorechnung} onChange={(e) => setStornorechnung(e.target.checked)} data-testid="storno-stornorechnung" />
                Stornorechnung in sevDesk erstellen
              </label>
            )}
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" className="accent-slate-900 w-4 h-4" checked={mail} onChange={(e) => setMail(e.target.checked)} data-testid="storno-kunde-mail" />
              Kunden per Mail informieren{order.angebot_test_mode ? " (Testmodus – Mail an Sie)" : ""}
            </label>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="storno-cancel">Abbrechen</Button>
          <Button variant="destructive" onClick={submit} disabled={busy || (paid && !erstattung && !order.kostenlos)} data-testid="storno-submit">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <XCircle className="w-4 h-4 mr-2" />} Stornieren
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function PaidCancelBadge({ order }) {
  if (!order.storno_bezahlt) return null;
  const label = { erstattet: "bezahlt – erstattet", ausstehend: "bezahlt – Erstattung ausstehend", manuell_offen: "bezahlt – Erstattung offen", keine: "kostenlos – keine Erstattung" }[order.erstattung_status] || "bezahlt";
  const cls = order.erstattung_status === "manuell_offen" ? "bg-amber-50 text-amber-800 border-amber-200" : "bg-slate-100 text-slate-700 border-slate-300";
  return <span className={`text-xs px-2 py-0.5 rounded-full border ${cls}`} data-testid="auftrag-erstattung-badge">{label}</span>;
}
